/**
 * Authentication Service
 * Handles login, logout, and token management
 * Uses dual storage (localStorage + sessionStorage) for Safari resilience
 */

import api from './api';
import tokenStorage from '../utils/tokenStorage';

export const authService = {
  // Login user
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });

    if (response.success && response.token) {
      // Backend returns: { success, token, data: { id, firstName, lastName, email, role, ... } }
      tokenStorage.setToken(response.token);
      tokenStorage.setUser(response.data);

      // Check if organization is suspended (store info for DashboardLayout overlay)
      // Use primaryOrganization (multi-org), fall back to organization (backward-compat)
      const activeOrg = response.data?.primaryOrganization || response.data?.organization;
      if (activeOrg?.status === 'suspended') {
        try {
          localStorage.setItem('orgSuspended', 'true');
          localStorage.setItem('suspensionReason', activeOrg.suspensionReason || 'No reason provided');
          localStorage.setItem('suspendedAt', activeOrg.suspendedAt || '');
        } catch { /* ignore storage errors */ }
      } else {
        try {
          localStorage.removeItem('orgSuspended');
          localStorage.removeItem('suspensionReason');
          localStorage.removeItem('suspendedAt');
        } catch { /* ignore */ }
      }

      return { token: response.token, user: response.data };
    }

    throw new Error(response.error || 'Login failed');
  },

  // Register user
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);

    // For paid plans, backend returns checkoutUrl instead of token
    if (response.success && response.checkoutUrl) {
      return { checkoutUrl: response.checkoutUrl, data: response.data };
    }

    // For free plans, backend returns token
    if (response.success && response.token) {
      tokenStorage.setToken(response.token);
      tokenStorage.setUser(response.data);
      return { token: response.token, user: response.data };
    }

    throw new Error(response.error || 'Registration failed');
  },

  // Logout user
  logout: () => {
    tokenStorage.clearAll();
    // Clear suspension info
    try {
      localStorage.removeItem('orgSuspended');
      localStorage.removeItem('suspensionReason');
      localStorage.removeItem('suspendedAt');
    } catch { /* ignore */ }
  },

  // Get current user
  getCurrentUser: () => {
    return tokenStorage.getUser();
  },

  // Get auth token
  getToken: () => {
    return tokenStorage.getToken();
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    return !!tokenStorage.getToken();
  },

  // Get user profile from API
  getProfile: async () => {
    return api.get('/auth/me');
  },

  // Update user profile
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/updateprofile', profileData);

    if (response.success) {
      // Update dual storage with new user data
      tokenStorage.setUser(response.data);
    }

    return response;
  },

  // Update password
  updatePassword: async (currentPassword, newPassword) => {
    return api.put('/auth/updatepassword', { currentPassword, newPassword });
  },

  // Check if email exists
  checkEmail: async (email) => {
    return api.post('/auth/check-email', { email });
  },

  // Forgot password
  forgotPassword: async (email) => {
    return api.post('/auth/forgot-password', { email });
  },

  // Reset password
  resetPassword: async (token, password) => {
    return api.post(`/auth/reset-password/${token}`, { password });
  },

  // Confirm email using token from the verification link
  verifyEmail: async (token) => {
    return api.post(`/auth/verify-email/${token}`);
  },

  // Re-send the email verification link (e.g. user lost the original)
  resendEmailVerification: async (email) => {
    return api.post('/auth/resend-verification', { email });
  },

  // Get user settings
  getSettings: async () => {
    return api.get('/auth/settings');
  },

  // Update notification settings
  updateNotificationSettings: async (settings) => {
    return api.put('/auth/settings/notifications', settings);
  },

  // Update preferences
  updatePreferences: async (preferences) => {
    return api.put('/auth/settings/preferences', preferences);
  },

  // Toggle two-factor authentication
  toggleTwoFactor: async (enabled) => {
    return api.put('/auth/settings/security/2fa', { enabled });
  },

  // Switch active organization (multi-org support)
  switchOrg: async (organizationId) => {
    const response = await api.post('/auth/switch-org', { organizationId });

    if (response.success) {
      // Update stored user data with new primary organization
      const user = tokenStorage.getUser();
      if (user) {
        user.primaryOrganization = response.data?.primaryOrganization || response.data?.organization;
        // Keep backward-compat field in sync
        user.organization = user.primaryOrganization;
        tokenStorage.setUser(user);
      }

      // Update token if a new one was issued
      if (response.token) {
        tokenStorage.setToken(response.token);
      }
    }

    return response;
  },

  // Get the active organization ID from stored user data
  getActiveOrgId: () => {
    const user = tokenStorage.getUser();
    if (!user) return null;
    const org = user.primaryOrganization || user.organization;
    if (!org) return null;
    // org may be a populated object or just an ID string
    return org._id || org;
  },

  // Fix #12: silently refresh the JWT so active users don't hit natural expiry.
  // Safe to call periodically — server validates current token before issuing new one.
  // Returns the new token on success, null on failure (existing token stays valid).
  refreshToken: async () => {
    try {
      const response = await api.post('/auth/refresh');
      if (response?.token) {
        tokenStorage.setToken(response.token);
        console.log('[Auth] Token refreshed proactively');
        return response.token;
      }
      return null;
    } catch (err) {
      // Silent fail — the existing token continues to work until it expires naturally
      console.warn('[Auth] Token refresh failed:', err?.message || err);
      return null;
    }
  },
};

export default authService;
